import { generateUUID } from '@/lib/utils';

import type { AssetType } from '@/types/asset';
import type { GetPageType, TrackEventParams } from './types';

const SESSION_STORAGE_KEY = 'sessionId';
const SESSION_UUID_KEY = 'sessionUuid';

/**
 * Creates a session and stores the session ID in the session storage.
 * @returns {boolean} Returns true if the session was successfully created, false otherwise.
 */
export const createTrackingSession = () => {
  let sessionId: string | undefined;

  // generateUUID already falls back to a non-crypto UUID when crypto.randomUUID
  // is unavailable (e.g. non-HTTPS dev), so one branch covers every environment.
  if (typeof window !== 'undefined' && typeof crypto !== 'undefined') {
    try {
      sessionId = generateUUID();
    } catch (e) {
      console.warn(e);
    }
  }

  if (sessionId && typeof window !== 'undefined') {
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }

  return sessionId ?? null;
};
/**
 * Gets the session ID from the session storage.
 * @returns {string | null} Returns the session ID if it exists, null otherwise.
 */

export const getSessionId = () => {
  if (typeof window !== 'undefined') {
    return sessionStorage.getItem(SESSION_STORAGE_KEY);
  }

  return null;
};

export const getPageType = (slug: string): GetPageType | null => {
  // SFX Tags
  if (slug.includes('/sfx/tag/')) {
    return {
      assetType: 'sfx',
      contentType: 'Tag',
    };
  }

  // Individual track.
  if (slug.startsWith('/music/tracks/')) {
    return {
      assetType: 'music',
      contentType: 'Track',
    };
  }

  // Music Tags
  if (
    slug.includes('/music/tag/') ||
    slug.includes('/music/genre/') ||
    slug.includes('/music/mood/') ||
    slug.includes('/music/theme/') ||
    slug.includes('/music/instrument/')
  ) {
    return {
      assetType: 'music',
      contentType: 'Tag',
    };
  }

  // SFX Collections
  if (slug.includes('/sfx/collection/')) {
    return {
      assetType: 'sfx',
      contentType: 'Collection',
    };
  }

  // SFX Styles
  if (slug.includes('/browse/sfx/')) {
    return {
      assetType: 'sfx',
      contentType: 'Style',
    };
  }

  // Music Styles
  if (slug.includes('/browse/music/')) {
    return {
      assetType: 'music',
      contentType: 'Style',
    };
  }

  // Contributor page. Asset type is inferred from ?type=... when present;
  // otherwise default to music to match the legacy /browse/artist behaviour.
  // Parse the query param explicitly (rather than substring-matching the URL)
  // so that filter expressions like ?filter=type:stock-video don't get
  // misread as a tab selection.
  if (slug.includes('/contributors/')) {
    const typeMatch = slug.match(/[?&]type=([^&]+)/);
    const contributorType = typeMatch ? decodeURIComponent(typeMatch[1]) : null;
    const contributorAssetType: AssetType =
      contributorType === 'stock-video'
        ? 'stock_footage'
        : contributorType === 'sfx'
          ? 'sfx'
          : contributorType === 'motion-graphics' || contributorType === 'luts'
            ? 'motion'
            : 'music';
    return {
      assetType: contributorAssetType,
      contentType: 'Artist',
    };
  }

  // Music Collections
  if (slug.includes('/music/collection/')) {
    return {
      assetType: 'music',
      contentType: 'Collection',
    };
  }

  // Search page.
  if (slug.includes('/search')) {
    return {
      assetType: 'general',
      contentType: 'Search',
    };
  }

  // SFX Page
  if (slug.includes('/sfx/')) {
    return {
      assetType: 'sfx',
      contentType: 'Track',
    };
  }

  if (slug.includes('/similar/')) {
    return {
      assetType: 'music',
      contentType: 'Similar',
    };
  }

  if (slug.includes('/playlist-generator')) {
    return {
      assetType: 'music',
      contentType: 'AIPG',
    };
  }

  if (slug.includes('/trending')) {
    return {
      assetType: 'general',
      contentType: 'Trending',
    };
  }

  // Stock Video Asset
  if (slug.includes('/stock-video/asset/')) {
    return {
      assetType: 'stock_footage',
      contentType: 'Stock Video',
    };
  }

  // Stock Video Category
  if (slug.includes('/stock-video/')) {
    return {
      assetType: 'stock_footage',
      contentType: 'Style',
    };
  }

  // Motion Graphics
  if (
    slug.includes('/motion-graphics/asset/') ||
    slug.includes('/luts/asset/')
  ) {
    return {
      assetType: 'motion',
      contentType: 'Motion Graphic',
    };
  }

  // Motion Graphics Category
  if (slug.includes('/motion-graphics/') || slug.includes('/luts/')) {
    return {
      assetType: 'motion',
      contentType: 'Style',
    };
  }

  return null;
};

/**
 * Tracks an event.
 * @param {TrackEventParams} params - The parameters for tracking the event.
 * @returns {Promise<void>} - A promise that resolves when the event is tracked successfully.
 *
 * @deprecated This is no longer used.
 */

// biome-ignore lint/suspicious/useAwait: deprecated
export const trackInternalEvent = async ({
  eventId,
  type,
}: TrackEventParams): Promise<void> => {
  console.warn('trackInternalEvent is deprecated');

  return;

  // const uuid = getUUID();

  // // If there is no session ID, do not track.
  // if (!uuid) {
  //   return;
  // }

  // try {
  //   await fetch(`${process.env.NEXT_PUBLIC_API}tracking/incrementresolve`, {
  //     method: 'POST',
  //     headers: {
  //       'Content-Type': 'application/json',
  //     },
  //     body: JSON.stringify({
  //       uuid,
  //       tracking_event_id: eventId ?? null,
  //       event_type: type,
  //     }),
  //   });
  // } catch (e) {
  //   console.warn(e);
  // }
};
